import { dataSourcesStardogRequestDispatchers } from 'src/actions/data-sources/actionCreators';
import { DataSourcesActionType } from 'src/actions/data-sources/actionTypes';
import { limitConcurrentRequests } from 'src/constants/limits';
import { Suggestion } from 'src/containers/ProjectMapping/types';
import { diagramSnapshotSelector } from 'src/selectors/diagramSnapshot';
import { connectionAsyncActions } from 'src/store/connections/connections.slice';
import { Connection } from 'src/store/connections/types';
import { ProjectResource } from 'src/store/state/projects';
import { AppDispatch } from 'src/store/store';
import { ReduxStateGetter } from 'src/store/types';
import { JsonLdResult } from 'src/templates/queries/types';
import { parseSuggestionResults } from 'src/utils/jsonld/parseSuggestionResults';
import { createSuggestionsRequestContents } from 'src/utils/suggestions/createSuggestionsRequestContents';
import { getClassIrisForSuggestionsRequest } from 'src/utils/suggestions/getClassIrisForSuggestionsRequest';

/**
 * Requests suggestions for resources by connecting to the stardog server
 * that each resource belongs to.
 */
export const requestSuggestions =
  ({
    classIris,
    connections,
    defaultConnection,
    projectId,
    resourceIds,
  }: {
    classIris: string[];
    /**
     * List of connections to lookup and connect to by a resource's `connectionId`.
     *
     * `defaultConnection` will be used if `connections` is undefined,
     * or when a resource's `connectionId` isn't found in this list.
     */
    connections?: Connection[];
    /**
     * The connection to use for ProjectFileUpload's and resources with missing or
     * invalid `connectionId`'s.
     */
    defaultConnection: Connection;
    projectId: string;
    resourceIds: string[];
  }) =>
  async (dispatch: AppDispatch, getState: ReduxStateGetter) => {
    const state = getState();

    const project = state.projects.projectsById[projectId];
    if (!project) {
      console.warn('!project');
      return null;
    }

    const diagram = diagramSnapshotSelector(projectId)(state);
    if (!diagram) {
      console.warn('!diagram');
      return null;
    }
    const { model } = diagram;

    if (connections) {
      const resourceConnections = new Set<Connection>();
      resourceIds.forEach((resourceId) => {
        const foundConnection = connections.find(
          ({ id }) => id === project.virtualGraphsById[resourceId]?.connectionId
        );
        if (foundConnection) {
          resourceConnections.add(foundConnection);
        }
      });

      await Promise.all(
        Array.from(resourceConnections).map(async (connection) => {
          if (connection.id !== defaultConnection.id) {
            await limitConcurrentRequests(async () => {
              await dispatch(
                connectionAsyncActions.requestServerStatus({
                  connection,
                })
              );
            });
          }
        })
      );
    }

    const {
      dataSources: { typeDescriptionByConnectionId },
      features: { stardogByConnectionId },
    } = getState();
    const classIrisForSuggestionsRequest = getClassIrisForSuggestionsRequest(
      classIris,
      model
    );

    const requestContents = createSuggestionsRequestContents({
      classIris: classIrisForSuggestionsRequest,
      defaultConnectionId: defaultConnection.id,
      model,
      project,
      resourceIds,
      stardogFeaturesByConnectionId: stardogByConnectionId,
      typeDescriptionByConnectionId,
    });

    const suggestions: Suggestion[] = [];

    await Promise.all(
      requestContents.map(
        async ({
          connectionId,
          content,
          resource: requestContentResource,
          tableNameToResourceMap,
        }) =>
          limitConcurrentRequests(async () => {
            const connection =
              connections?.find(({ id }) => id === connectionId) ||
              defaultConnection;

            const stardogFeatures = connectionId
              ? stardogByConnectionId[connectionId]
              : null;

            const response =
              await dataSourcesStardogRequestDispatchers.requestSuggestions({
                connection,
                content,
              });

            if (
              response.type ===
              DataSourcesActionType.REQUEST_SUGGESTIONS_SUCCESS
            ) {
              const body: JsonLdResult[] = response.payload.response?.body;
              const suggestionsWithoutResource = parseSuggestionResults(
                body,
                classIris.length ? model : null,
                stardogFeatures?.isDataSourceRelationshipSuggestionsSupported
                  ? false
                  : resourceIds.length > 1 && !classIris.length
              );
              suggestionsWithoutResource.forEach((suggestion) => {
                let resource: ProjectResource | undefined;
                if (requestContentResource) {
                  resource = requestContentResource;
                } else if (suggestion.source && tableNameToResourceMap) {
                  resource = tableNameToResourceMap[suggestion.source];
                }
                if (resource) {
                  suggestions.push({ ...suggestion, resource });
                } else {
                  console.warn(`couldn't match a suggestion to a resource`, {
                    suggestion,
                    requestContentResource,
                    tableNameToResourceMap,
                  });
                }
              });
            }
          })
      )
    );

    return suggestions;
  };
